/**
 * @fileoverview Org portal: what this organization tells its instructors by
 * email and text.
 *
 * Four notifications, each on or off, each by email, text or both:
 * a cancelled class, program announcements, class announcements, and
 * missed lesson notes. Missed notes also chooses how many alerts go out and
 * how long after the session each one is: a number and a unit (minutes, hours
 * or days), so "1 hour" and "3 days" sit side by side, up to 14 days.
 *
 * Whatever is switched here, the same notice also lands in each instructor's
 * Updates on Hans; these switches are only about the inbox and the phone.
 *
 * Each change saves on its own, optimistically, and rolls back on failure,
 * the Features page's rule: a switch that claims to have saved and did not is
 * worse than one that is slow. Saves go out one at a time and a refusal goes
 * back to what the server last confirmed (useSaveQueue), so two quick changes
 * never undo each other. The last channel of a switched-on notification
 * cannot be unticked, because "on, by neither" is a switch that sends nothing.
 *
 * Under them, when each program's weekly recap and week ahead go out
 * (ProgramEmailSchedule). Those go to the program's people, not its
 * instructors, and used to sit under Settings → Features.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader } from "@/components/studio/primitives";
import { Toggle } from "@/components/settings/SettingsSection";
import ProgramEmailSchedule from "@/components/org/ProgramEmailSchedule";
import useSaveQueue from "@/components/org/useSaveQueue";

/** The settings with one notification's section changed by `patch`. */
const withSection = (data, event, patch) => ({
  ...data,
  settings: { ...data.settings, [event]: { ...data.settings?.[event], ...patch } },
});

/** The notifications, in the order an admin thinks about them. */
export const NOTIFICATIONS = [
  {
    id: "class_cancelled",
    label: "Cancelled class",
    desc: "When you cancel a meeting of a class, its instructors are told. A past meeting cancelled to correct the record is not sent.",
  },
  {
    id: "program_announcement",
    label: "Program announcements",
    desc: "Announcements sent from a program's page. You choose who receives each one, and whether it is emailed or texted, when you send it.",
  },
  {
    id: "class_announcement",
    label: "Class announcements",
    desc: "Announcements sent to the instructors of the classes you choose.",
  },
  {
    id: "notes_missed",
    label: "Lesson notes missed",
    desc: "Remind instructors when a session has no lesson notes. The alert links straight to that session: tap Add notes, write, press Save.",
  },
];

const SELECT =
  "rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)] disabled:opacity-50";

/** Minutes in one of each unit an alert can be written in. */
export const UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440 };

/** The unit choices, singular and plural so "1 hour" never reads "1 hours". */
export const UNITS = [
  { id: "minutes", one: "minute", many: "minutes" },
  { id: "hours", one: "hour", many: "hours" },
  { id: "days", one: "day", many: "days" },
];

/** How long after the session an alert goes out, in minutes. */
export const alertMinutes = (a) => Number(a.amount) * UNIT_MINUTES[a.unit];

/** "1 hour", "90 minutes", "3 days". */
export function alertLabel(a) {
  const unit = UNITS.find((u) => u.id === a.unit) || UNITS[2];
  return `${a.amount} ${Number(a.amount) === 1 ? unit.one : unit.many}`;
}

/**
 * The alerts as this page edits them. A server a release behind answers with
 * whole days only (`alert_days`), which read as that many days.
 */
export function alertsFrom(notes) {
  if (Array.isArray(notes?.alerts) && notes.alerts.length) return notes.alerts.map((a) => ({ ...a }));
  return (notes?.alert_days?.length ? notes.alert_days : [1]).map((d) => ({ amount: d, unit: "days" }));
}

/** A timing written in the largest unit that holds it exactly. */
function inUnits(minutes) {
  if (minutes % UNIT_MINUTES.days === 0) return { amount: minutes / UNIT_MINUTES.days, unit: "days" };
  if (minutes % UNIT_MINUTES.hours === 0) return { amount: minutes / UNIT_MINUTES.hours, unit: "hours" };
  return { amount: minutes, unit: "minutes" };
}

/**
 * The alert to add after `last`: a day later, or an hour, or a minute when
 * the 14-day ceiling leaves no more room; null when there is none at all.
 */
export function nextAlert(last, maxMinutes) {
  const from = last ? alertMinutes(last) : 0;
  for (const step of [UNIT_MINUTES.days, UNIT_MINUTES.hours, UNIT_MINUTES.minutes]) {
    if (from + step <= maxMinutes) return inUnits(from + step);
  }
  return null;
}

/** `count` alerts: the ones already chosen, then each new one after the last. */
export function alertsForCount(current, count, maxMinutes) {
  const out = current.slice(0, count).map((a) => ({ ...a }));
  while (out.length < count) {
    const next = nextAlert(out[out.length - 1], maxMinutes);
    if (!next) break;
    out.push(next);
  }
  return out;
}

/**
 * What is wrong with each alert, or null for one that is fine. An alert is a
 * whole number of at least one, no later than the ceiling, and later than
 * the one above it: the order the admin sees is the order they get.
 */
export function alertErrors(alerts, maxMinutes, maxDays) {
  return alerts.map((a, i) => {
    const n = Number(a.amount);
    if (!Number.isInteger(n) || n < 1) return "Enter a whole number, 1 or more.";
    if (alertMinutes(a) > maxMinutes) return `No later than ${maxDays} days after the session.`;
    if (i > 0 && alertMinutes(a) <= alertMinutes(alerts[i - 1])) return `Must come after alert ${i}.`;
    return null;
  });
}

/** "1 hour, 1 day and 3 days". */
export function alertSummary(alerts) {
  const labels = alerts.map(alertLabel);
  return labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0] || "";
}

/**
 * The fill-in-the-blank rows for missed-notes alerts: "Alert 1 [ 1 ] [hours]
 * after the session ends". Typing edits a draft; leaving the box, pressing
 * Enter or picking a unit saves it, and only when every alert is valid, so a
 * half-typed number is never sent.
 */
function NotesAlertTiming({ notes, limits, onSave }) {
  const maxDays = limits.max_days;
  const maxMinutes = maxDays * UNIT_MINUTES.days;
  const saved = useMemo(() => alertsFrom(notes), [notes]);
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState(saved);
  const [touched, setTouched] = useState(false);

  // A save (or its rollback) is the new truth; switching another section's
  // channel does not change the alerts, so it never resets a draft.
  useEffect(() => {
    setDraft(JSON.parse(savedKey));
    setTouched(false);
  }, [savedKey]);

  const errors = alertErrors(draft, maxMinutes, maxDays);

  const commit = (next) => {
    setTouched(true);
    if (alertErrors(next, maxMinutes, maxDays).some(Boolean)) return;
    const clean = next.map((a) => ({ amount: Number(a.amount), unit: a.unit }));
    if (JSON.stringify(clean) !== savedKey) onSave({ alerts: clean });
  };

  const edit = (index, patch) => setDraft((d) => d.map((a, i) => (i === index ? { ...a, ...patch } : a)));

  return (
    <div className="mt-5 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="notes-alert-count" className="font-sans text-[14px] text-[var(--text-primary)]">
          Number of alerts
        </label>
        <select
          id="notes-alert-count"
          data-testid="notes-alert-count"
          value={draft.length}
          onChange={(e) => {
            const base = errors.some(Boolean) ? saved : draft;
            const next = alertsForCount(base, Number(e.target.value), maxMinutes);
            setDraft(next);
            commit(next);
          }}
          className={SELECT}
        >
          {Array.from({ length: limits.max_alerts }, (_, k) => k + 1).map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {draft.map((a, idx) => {
          const error = touched ? errors[idx] : null;
          const plural = Number(a.amount) !== 1;
          return (
            <div key={idx} data-testid={`notes-alert-${idx}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="w-16 font-sans text-[13px] text-[var(--text-tertiary)]">Alert {idx + 1}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={a.amount}
                  aria-label={`Alert ${idx + 1} amount`}
                  aria-invalid={error ? "true" : undefined}
                  data-testid={`notes-alert-amount-${idx}`}
                  onChange={(e) => edit(idx, { amount: e.target.value })}
                  onBlur={() => commit(draft)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commit(draft);
                    }
                  }}
                  className={`${SELECT} w-20 font-mono tabular-nums ${error ? "border-[var(--state-danger-border)]" : ""}`}
                />
                <select
                  aria-label={`Alert ${idx + 1} unit`}
                  data-testid={`notes-alert-unit-${idx}`}
                  value={a.unit}
                  onChange={(e) => {
                    const next = draft.map((x, i) => (i === idx ? { ...x, unit: e.target.value } : x));
                    setDraft(next);
                    commit(next);
                  }}
                  className={`${SELECT} w-[118px]`}
                >
                  {UNITS.map((u) => (
                    <option key={u.id} value={u.id}>
                      {plural ? u.many : u.one}
                    </option>
                  ))}
                </select>
                <span className="font-sans text-[14px] text-[var(--text-secondary)]">after the session ends</span>
              </div>
              {error && (
                <p className="mt-1 font-sans text-[12px] text-[var(--state-danger)] sm:pl-[76px]" role="alert" data-testid={`notes-alert-error-${idx}`}>
                  {error}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!errors.some(Boolean) && (
        <p className="mt-4 font-sans text-[13px] text-[var(--text-secondary)]" data-testid="notes-alert-summary">
          Instructors are reminded {alertSummary(draft)} after a session with no notes.
        </p>
      )}
      <p className="mt-2 font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
        Timed from the end of the session, up to {maxDays} days after. Hans checks for missing notes once an hour, so
        each alert goes out within the hour after the time you set. Alerts stop as soon as notes are saved for the
        session, or a substitute is recorded against it. Prep notes don&rsquo;t count as lesson notes: they stay in
        place and become the lesson when the instructor presses Save.
      </p>
    </div>
  );
}

function Channel({ label, checked, disabled, onChange, testId, hint }) {
  return (
    <label
      className={`flex items-center gap-2 font-sans text-[14px] ${disabled ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"}`}
      title={hint || undefined}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="accent-[var(--accent-yellow)]"
      />
      {label}
    </label>
  );
}

/**
 * What the organization tells its instructors: the four notifications, each
 * saved through the page's `save`, and who receives them.
 */
function InstructorNotifications({ data, save }) {
  const { settings, limits = { max_alerts: 5, max_days: 14 } } = data;
  const notes = settings.notes_missed;

  return (
    <>
      {!data.delivery_enabled && (
        <Card className="mb-6" data-testid="notifications-delivery-off">
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">
            Email and text delivery to instructors isn&rsquo;t switched on for your account yet. Your choices are saved
            now and take effect as soon as it is.
          </p>
        </Card>
      )}

      <Card padding={0}>
        {NOTIFICATIONS.map((n, i) => {
          const s = settings[n.id];
          const onlyEmail = s.email && !s.sms;
          const onlySms = s.sms && !s.email;
          return (
            <div
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`px-5 py-5 md:px-6 ${i < NOTIFICATIONS.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{n.label}</div>
                  <div className="mt-1 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">{n.desc}</div>
                </div>
                <Toggle
                  on={s.enabled}
                  label={n.label}
                  testId={`notification-${n.id}-toggle`}
                  // Switching on with neither channel would be refused; email
                  // is the one every instructor has.
                  onChange={(on) => save(n.id, on && !s.email && !s.sms ? { enabled: true, email: true } : { enabled: on })}
                />
              </div>

              {s.enabled && (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <Channel
                    label="Email"
                    checked={s.email}
                    disabled={onlyEmail}
                    testId={`notification-${n.id}-email`}
                    onChange={(v) => save(n.id, { email: v })}
                  />
                  <Channel
                    label="Text message"
                    checked={s.sms}
                    disabled={onlySms || (!s.sms && !data.sms_available)}
                    hint={!data.sms_available ? data.sms_unavailable_reason : null}
                    testId={`notification-${n.id}-sms`}
                    onChange={(v) => save(n.id, { sms: v })}
                  />
                  {!data.sms_available && (
                    <span className="w-full font-sans text-[12px] text-[var(--text-tertiary)]" data-testid={`notification-${n.id}-sms-reason`}>
                      {data.sms_unavailable_reason}
                    </span>
                  )}
                </div>
              )}

              {n.id === "notes_missed" && s.enabled && (
                <NotesAlertTiming notes={notes} limits={limits} onSave={(patch) => save("notes_missed", patch)} />
              )}
            </div>
          );
        })}
      </Card>

      <SectionHeader className="mt-8">Who receives these</SectionHeader>
      <p className="font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
        Instructors, at the email and phone number on their Hans profile. Texts use your plan&rsquo;s monthly text
        allowance, and anyone can reply STOP to stop receiving them. Program viewers receive announcements by email.
        Other admins get a copy of announcements by email; each admin can turn that off in Settings &rsaquo;
        Preferences.
      </p>
    </>
  );
}

export default function OrgNotificationsPage() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // Once no save is left in flight, the page shows what the server confirmed.
  const { run, confirm } = useSaveQueue(setData);

  useEffect(() => {
    orgApi
      .get("/org/notification-settings")
      .then((res) => {
        confirm(res.data);
        setData(res.data);
      })
      .catch(() => setFailed(true));
  }, [confirm]);

  /* Save one section's change: shown at once, sent after any save before it,
     and put back to what the server last confirmed if it is refused. */
  const save = useCallback(
    async (event, patch) => {
      setData((d) => withSection(d, event, patch));
      try {
        await run(async (current) => {
          const res = await orgApi.put("/org/notification-settings", { [event]: patch });
          return res.data?.settings ? res.data : withSection(current, event, patch);
        });
      } catch (err) {
        toast.error(getOrgErrorMessage(err, "Couldn't save that"));
      }
    },
    [run],
  );

  // The program email times read the organization, not these settings, so
  // they stay on the page when the settings cannot be loaded.
  return (
    <div data-testid="org-notifications">
      <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Notifications</h1>
      <p className="mb-6 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
        Choose what your instructors are told by email and text, and when your program emails go out. What
        instructors are told also appears in their Updates on Hans, whether or not it is emailed.
      </p>

      {failed ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load your notification settings.</p>
      ) : data ? (
        <InstructorNotifications data={data} save={save} />
      ) : (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      )}

      <ProgramEmailSchedule />
    </div>
  );
}
